import { css } from '@emotion/react';
import cn from 'classnames';
import { type ForwardedRef, forwardRef, memo, type ReactNode } from 'react';
import type { To } from 'react-router-dom';

import { Typography } from '../../../general/typography/Typography';
import { Link } from '../../link/Link';

export enum ELEMENT_VARIANTS {
  SECTION = 'SECTION',
  SUB_SECTION = 'SUB_SECTION',
  ITEM = 'ITEM',
}

export type SimpletableTableOfContentsElementProps = {
  /** Element label. */
  label: ReactNode;
  /** Children. */
  children?: ReactNode;
  /** Link href. */
  href?: To;
  /** Is the element currently selected. */
  isSelected?: boolean;
  /** Element variant. */
  variant?: ELEMENT_VARIANTS;
};

export const SimpleTableOfContentsElementBase = forwardRef(
  (
    {
      children = null,
      label,
      href = null,
      isSelected = false,
      variant = ELEMENT_VARIANTS.SECTION,
    }: SimpletableTableOfContentsElementProps,
    ref: ForwardedRef<HTMLLIElement>,
  ) => (
    <li ref={ref}>
      <Link
        className={cn(variant, isSelected ? 'SELECTED' : null)}
        css={(theme) => css`
          position: relative;
          display: flex;
          text-decoration: none;
          color: inherit;
          padding: 4px 20px;
          border-left: 1px solid transparent;

          transition: ${theme.transitions.default()};

          &.SUB_SECTION {
            padding-left: 40px;
          }
          &.ITEM {
            padding-left: 60px;
          }
          &.SELECTED {
            border-left: 1px solid ${theme.colors.primary[500]};
            color: ${theme.colors.primary[500]};
          }
        `}
        isDisabled={!href}
        to={href}
      >
        <Typography variant={Typography.VARIANTS.BODY_MEDIUM_REGULAR}>{label}</Typography>
      </Link>
      {children && (
        <ul
          css={css`
            list-style-type: none;
            display: flex;
            flex-direction: column;
            gap: 4px;
          `}
        >
          {children}
        </ul>
      )}
    </li>
  ),
);

SimpleTableOfContentsElementBase.displayName = 'SimpleTableOfContentsElement';

export const SimpleTableOfContentsElement = memo(SimpleTableOfContentsElementBase);
